import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert,
  App,
  Card,
  Checkbox,
  Col,
  Flex,
  Row,
  Select,
  Switch,
  Typography
} from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsOrganizationsAuthMethodsPartialUpdateMutation,
  organizationsOrganizationsRetrieveOptions,
  organizationsOrganizationsRetrieveQueryKey
} from 'src/api/main'
import { SectionLabel, SoonTag } from 'src/components'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

const renderLabel = (label: string) => (
  <SectionLabel style={{ display: 'block', marginBottom: 4 }}>
    {label}
  </SectionLabel>
)

type Props = {
  workspaceId: number
  idValid: boolean
}

export const PolicyTab = ({ workspaceId, idValid }: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const showApiErrors = useShowApiErrors()

  const { data: organization, isLoading: orgLoading } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({ path: { id: workspaceId } }),
    enabled: idValid
  })
  const requireMfa = organization?.require_mfa ?? false

  // `require_mfa` is still read off the organization, but it is written through
  // the auth-methods serializer now — the plain organization patch stopped
  // accepting it.
  const { mutate: patchAuthMethods, isPending: savingRequireMfa } = useMutation(
    {
      ...organizationsOrganizationsAuthMethodsPartialUpdateMutation(),
      onSuccess: () => {
        message.success(t('information_updated_success'))
        invalidateQueries([
          organizationsOrganizationsRetrieveQueryKey({
            path: { id: workspaceId }
          })
        ])
      },
      onError: (error) => showApiErrors(error)
    }
  )

  return (
    <Flex vertical gap={14}>
      <Alert
        type="info"
        showIcon
        title={
          <Flex align="center" gap={6} wrap="wrap">
            <Text strong>
              {t('workspace.authentication.policy.shippingSoon')}
            </Text>
            <Text type="secondary">
              {t('workspace.authentication.policy.shippingSoonDescription')}
            </Text>
            <SoonTag />
          </Flex>
        }
      />

      <Card
        size="small"
        title={
          <Flex align="center" gap={6}>
            <span>{t('workspace.authentication.policy.password.title')}</span>
            <SoonTag />
          </Flex>
        }
      >
        <Flex vertical gap={12}>
          <Row gutter={14}>
            <Col xs={24} md={8}>
              {renderLabel(
                t('workspace.authentication.policy.password.minLength')
              )}
              <Select
                size="small"
                defaultValue={12}
                style={{ width: '100%' }}
                disabled
                options={[8, 10, 12, 14, 16].map((n) => ({
                  value: n,
                  label: t(
                    'workspace.authentication.policy.password.characters',
                    {
                      count: n
                    }
                  )
                }))}
              />
            </Col>
            <Col xs={24} md={8}>
              {renderLabel(
                t('workspace.authentication.policy.password.reusePrevention')
              )}
              <Select
                size="small"
                defaultValue={5}
                style={{ width: '100%' }}
                disabled
                options={[0, 3, 5, 10, 24].map((n) => ({
                  value: n,
                  label:
                    n === 0
                      ? t(
                          'workspace.authentication.policy.password.noRestriction'
                        )
                      : t('workspace.authentication.policy.password.lastN', {
                          count: n
                        })
                }))}
              />
            </Col>
            <Col xs={24} md={8}>
              {renderLabel(
                t('workspace.authentication.policy.password.maxAge')
              )}
              <Select
                size="small"
                defaultValue={90}
                style={{ width: '100%' }}
                disabled
                options={[0, 30, 60, 90, 180, 365].map((n) => ({
                  value: n,
                  label:
                    n === 0
                      ? t(
                          'workspace.authentication.policy.password.neverExpires'
                        )
                      : t('workspace.authentication.policy.password.days', {
                          count: n
                        })
                }))}
              />
            </Col>
          </Row>
          <Flex gap={6} wrap="wrap">
            <Checkbox disabled defaultChecked>
              {t('workspace.authentication.policy.password.requireUpper')}
            </Checkbox>
            <Checkbox disabled defaultChecked>
              {t('workspace.authentication.policy.password.requireLower')}
            </Checkbox>
            <Checkbox disabled defaultChecked>
              {t('workspace.authentication.policy.password.requireNumber')}
            </Checkbox>
            <Checkbox disabled defaultChecked>
              {t('workspace.authentication.policy.password.requireSymbol')}
            </Checkbox>
            <Checkbox disabled>
              {t('workspace.authentication.policy.password.rejectLeaked')}
            </Checkbox>
          </Flex>
        </Flex>
      </Card>

      <Card size="small" title={t('workspace.authentication.policy.mfa.title')}>
        <Flex vertical gap={10}>
          {/* Live: workspace-wide MFA requirement (Organization.require_mfa).
              Every member of this workspace is then required to complete MFA at
              login, composed with their own enrollment and any per-member
              override. */}
          <Flex align="center" justify="space-between" gap={12}>
            <Text style={{ fontSize: 13 }}>
              {t('workspace.authentication.policy.mfa.requireMfaAll')}
            </Text>
            <Switch
              checked={requireMfa}
              loading={savingRequireMfa}
              disabled={!idValid || orgLoading}
              onChange={(checked) =>
                patchAuthMethods({
                  path: { id: workspaceId },
                  body: { require_mfa: checked }
                })
              }
            />
          </Flex>

          {/* Not yet wired to the backend — future policy controls. */}
          {[
            { i18nKey: 'requireMfaAdmins', defaultOn: true },
            { i18nKey: 'allowSmsFallback', defaultOn: false },
            { i18nKey: 'requireWebAuthnOwners', defaultOn: false }
          ].map((row) => (
            <Flex
              key={row.i18nKey}
              align="center"
              justify="space-between"
              gap={12}
            >
              <Flex align="center" gap={6}>
                <Text style={{ fontSize: 13 }}>
                  {t(`workspace.authentication.policy.mfa.${row.i18nKey}`)}
                </Text>
                <SoonTag />
              </Flex>
              <Switch defaultChecked={row.defaultOn} disabled />
            </Flex>
          ))}
        </Flex>
      </Card>

      <Card
        size="small"
        title={
          <Flex align="center" gap={6}>
            <span>{t('workspace.authentication.policy.session.title')}</span>
            <SoonTag />
          </Flex>
        }
      >
        <Row gutter={14}>
          <Col xs={24} md={12}>
            {renderLabel(
              t('workspace.authentication.policy.session.idleTimeout')
            )}
            <Select
              size="small"
              defaultValue={60}
              style={{ width: '100%' }}
              disabled
              options={[15, 30, 60, 120, 240, 480].map((n) => ({
                value: n,
                label: t('workspace.authentication.policy.session.minutes', {
                  count: n
                })
              }))}
            />
          </Col>
          <Col xs={24} md={12}>
            {renderLabel(
              t('workspace.authentication.policy.session.absoluteTimeout')
            )}
            <Select
              size="small"
              defaultValue={720}
              style={{ width: '100%' }}
              disabled
              options={[60, 240, 720, 1440, 10080].map((n) => ({
                value: n,
                label:
                  n >= 1440
                    ? t('workspace.authentication.policy.session.days', {
                        count: Math.round(n / 1440)
                      })
                    : t('workspace.authentication.policy.session.minutes', {
                        count: n
                      })
              }))}
            />
          </Col>
        </Row>
      </Card>
    </Flex>
  )
}
